import { useEffect } from 'react'

/** Set on `<html>` while an immersive surface is open. See below for what reads it. */
export const IMMERSIVE_ATTRIBUTE = 'data-immersive-surface'

/**
 * Hides the app's mobile chrome — the global header and the bottom tab bar — for as long as
 * a surface that owns the whole screen is open.
 *
 * A conversation is the case this exists for. It arrives with a header of its own (a back
 * control and the channel name), so the global header above it was a second row saying
 * nothing the first did not, and the tab bar below it sat between the composer and the
 * keyboard. Together they cost 116px of a 844px phone before the notch and the keyboard are
 * counted, which is why barely one message was ever on screen. No native messaging app
 * keeps either one inside a thread; you go back to the list for navigation.
 *
 * Published as an attribute on `<html>` rather than passed down as a prop because the
 * chrome is not in this tree — MobileHeader and MobileNav are siblings of the whole page,
 * rendered by MainLayout. The same reason, and the same shape, as `data-keyboard-open` in
 * useVisualViewport. Surfaces react by redefining `--mobile-chrome-top`/`--mobile-chrome-bottom`
 * to zero in global.scss, so every layout that measures against those tokens reclaims the
 * space without knowing anything about this hook.
 *
 * @param isImmersive whether the surface is open AND the viewport is one where the mobile
 * chrome exists at all — pass the caller's `isMobile` check in, since above `md` the header
 * and tab bar are not rendered and hiding them would be meaningless.
 */
const useImmersiveSurface = (isImmersive: boolean): void => {
  useEffect(() => {
    if (!isImmersive) {
      return
    }

    document.documentElement.setAttribute(IMMERSIVE_ATTRIBUTE, '')

    // Always removed on the way out, so navigating away from a conversation — or crossing
    // the md breakpoint — brings the chrome back rather than stranding the app with no
    // navigation at all.
    return () => {
      document.documentElement.removeAttribute(IMMERSIVE_ATTRIBUTE)
    }
  }, [isImmersive])
}

export default useImmersiveSurface
